import type { MaybeRefOrGetter } from 'vue';
import type { Filter } from '#layers/data-model/public';
import { uniqBy } from 'lodash-es';
import { computed, ref, toValue } from 'vue';
import { isUuid, useThrottledSearchTerm } from '#layers/base/public';
import { isRelationalFilter } from '#layers/data-model/public';
import { useSystemEntityStore } from '#layers/runtime/public';
import { DEFAULT_LISTING_PAGE_SIZE } from '#layers/ui/public';

interface UseServiceAccountOptionsOptions {
	/** Limit accounts to members of this workspace; disables loading while unset. */
	workspaceId?: MaybeRefOrGetter<string | null | undefined>;
	/** Account whose label must remain hydrated outside the current result page. */
	selectedId?: MaybeRefOrGetter<string | null | undefined>;
}

/** Creates a workspace-aware service-account filter for a search or selected ID. */
function createServiceAccountFilter(workspaceId: string | null | undefined, search: string, selectedId?: string) {
	const filters: Filter[] = [];

	if (workspaceId) {
		filters.push({ workspaces: { _some: { workspaceId } } });
	}

	if (selectedId) {
		filters.push({ id: { _eq: selectedId } });
	}
	else if (search) {
		filters.push(isUuid(search)
			? {
					_or: [
						{ fullName: { _icontains: search } },
						{ id: { _eq: search } },
					],
				}
			: { fullName: { _icontains: search } });
	}

	if (filters.length === 0) return;
	if (filters.length === 1) return filters[0];
	return { _and: filters };
}

/**
 * Loads a throttled first page of service accounts and keeps an optional
 * selected account hydrated for remote select menus.
 */
export function useServiceAccountOptions(options: UseServiceAccountOptionsOptions = {}) {
	const store = useSystemEntityStore();
	const searchTerm = ref('');
	const { normalizedSearchTerm, currentSearchTerm } = useThrottledSearchTerm(searchTerm);
	const workspaceId = computed(() => toValue(options.workspaceId));
	const selectedId = computed(() => toValue(options.selectedId));
	const hasWorkspaceScope = options.workspaceId !== undefined;
	const listEnabled = computed(() => !hasWorkspaceScope || Boolean(workspaceId.value));

	const listQuery = store.MonospaceServiceAccount.query((q) => {
		if (!listEnabled.value) return q.many({ enabled: false });

		const filter = createServiceAccountFilter(workspaceId.value, normalizedSearchTerm.value);

		return q.many({
			filter,
			// Relational workspace scoping cannot be resolved by the local cache.
			fetchPolicy: isRelationalFilter(filter) ? 'no-cache' : 'cache-and-fetch',
			pageIndex: 0,
			pageSize: DEFAULT_LISTING_PAGE_SIZE,
			resultMode: 'responseRefs',
			sort: [
				{ fullName: { direction: 'asc' } },
				{ id: { direction: 'asc' } },
			],
		});
	});

	const selectedQuery = store.MonospaceServiceAccount.query((q) => {
		if (!selectedId.value || !listEnabled.value) return q.first({ enabled: false });

		const filter = createServiceAccountFilter(workspaceId.value, '', selectedId.value);

		return q.first({
			filter,
			...(isRelationalFilter(filter) ? { fetchPolicy: 'no-cache' as const } : {}),
		});
	});

	const serviceAccounts = computed(() => {
		if (!listEnabled.value) return [];

		const currentSelectedId = selectedId.value;
		const selected = currentSelectedId && selectedQuery.data.value?.id === currentSelectedId
			? selectedQuery.data.value
			: undefined;
		const searchSettled = currentSearchTerm.value === normalizedSearchTerm.value;
		const accounts = searchSettled && !listQuery.loading.value ? (listQuery.data.value ?? []) : [];
		if (!selected) return accounts;

		return uniqBy([selected, ...accounts], 'id');
	});

	const serviceAccountOptions = computed(() => serviceAccounts.value.map((account) => ({
		label: account.fullName ?? account.id,
		id: account.id,
	})));

	const isLoading = computed(() => listEnabled.value && (
		currentSearchTerm.value !== normalizedSearchTerm.value
		|| listQuery.loading.value
		|| (Boolean(selectedId.value) && selectedQuery.loading.value)
	));

	/** Refreshes both the current result page and selected-account hydration. */
	async function refresh() {
		await Promise.all([listQuery.refresh(), selectedQuery.refresh()]);
	}

	return {
		isLoading,
		refresh,
		searchTerm,
		serviceAccounts,
		serviceAccountOptions,
	};
}
